import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import LabelIcon from "@mui/icons-material/Label";
import { useAuthApi } from "../hooks/useAuthApi";
import { OCPP, MGMT } from "../env";
import { fmtDate, statusColor } from "../format";
import type { Charger, ChargerConfig, CountedPage, Label } from "../types";
import { useToast } from "../toast";
import ChargerActionsDialog from "../dialogs/ChargerActionsDialog";
import LabelDialog from "../dialogs/LabelDialog";

export default function Chargers({ onOpenDetail }: { onOpenDetail?: (id: number) => void }) {
  const api = useAuthApi();
  const [items, setItems] = useState<Charger[]>([]);
  const [configs, setConfigs] = useState<ChargerConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Charger | null>(null);

  // Label management
  const [labelConfig, setLabelConfig] = useState<ChargerConfig | null>(null);
  const [configLabels, setConfigLabels] = useState<Record<number, Label[]>>({});
  const [addingLabelTo, setAddingLabelTo] = useState<ChargerConfig | null>(null);

  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [chargers, cfgs] = await Promise.all([
        api<Charger[]>(`${OCPP}/chargers`),
        api<CountedPage<ChargerConfig>>(`${MGMT}/charger-configs`),
      ]);
      const allChargers = chargers ?? [];
      const allConfigs = cfgs?.items ?? [];
      setConfigs(allConfigs);
      const assignedIds = new Set(
        allConfigs.filter((c) => c.location_id != null).map((c) => c.charge_point_id)
      );
      setItems(allChargers.filter((c) => assignedIds.has(c.charge_point_id)));
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const loadLabels = useCallback(
    async (cfg: ChargerConfig) => {
      try {
        const data = await api<Label[]>(`${MGMT}/charger-configs/${cfg.id}/labels`);
        setConfigLabels((prev) => ({ ...prev, [cfg.id]: data ?? [] }));
        setLabelConfig(cfg);
      } catch (e) {
        toast.show((e as Error).message, "error");
      }
    },
    [toast]
  );

  const deleteLabel = async (cfg: ChargerConfig, labelId: number) => {
    try {
      await api(`${MGMT}/charger-configs/${cfg.id}/labels/${labelId}`, { method: "DELETE" });
      toast.show("Label removed");
      loadLabels(cfg);
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  };

  const configFor = (charger: Charger) =>
    configs.find((c) => c.charge_point_id === charger.charge_point_id);

  return (
    <>
      <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Charge Point ID</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Vendor / Model</TableCell>
              <TableCell>OCPP</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Connection</TableCell>
              <TableCell>Config Sync</TableCell>
              <TableCell>Labels</TableCell>
              <TableCell>Last Heartbeat</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  No assigned chargers yet.
                </TableCell>
              </TableRow>
            )}
            {items.map((c) => {
              const cfg = configFor(c);
              const labels = cfg ? (configLabels[cfg.id] ?? cfg.labels ?? []) : [];
              return (
                <TableRow
                  key={c.id}
                  hover
                  sx={{ cursor: cfg && onOpenDetail ? "pointer" : undefined }}
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest("button")) return;
                    if (cfg) onOpenDetail?.(cfg.id);
                  }}
                >
                  <TableCell sx={{ fontFamily: "monospace" }}>
                    {c.charge_point_id}
                  </TableCell>
                  <TableCell>{cfg?.location?.name ?? "–"}</TableCell>
                  <TableCell>
                    {[c.vendor, c.model].filter(Boolean).join(" / ") || "–"}
                  </TableCell>
                  <TableCell>{c.ocpp_version ?? "–"}</TableCell>
                  <TableCell>
                    <Chip label={c.status} color={statusColor(c.status)} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={c.is_connected ? "Connected" : "Offline"}
                      color={c.is_connected ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {cfg?.config_template_id ? (
                      <Chip
                        label={cfg.config_sync_state ?? "unknown"}
                        size="small"
                        color={cfg.config_sync_state === "in_sync" ? "success" : "default"}
                        variant={cfg.config_sync_state === "out_of_sync" ? "filled" : "outlined"}
                      />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center">
                      {labelConfig?.id === cfg?.id &&
                        labels.map((lbl) => (
                          <Chip
                            key={lbl.id}
                            label={`${lbl.key}: ${lbl.value}`}
                            size="small"
                            variant="outlined"
                            onDelete={() => cfg && deleteLabel(cfg, lbl.id)}
                          />
                        ))}
                      {cfg && (
                        <IconButton
                          size="small"
                          title="Manage labels"
                          onClick={() => {
                            setAddingLabelTo(cfg);
                            loadLabels(cfg);
                          }}
                        >
                          <LabelIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>{fmtDate(c.last_heartbeat)}</TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => setSelected(c)}>
                      Actions
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <ChargerActionsDialog charger={selected} onClose={() => setSelected(null)} />

      <LabelDialog
        open={addingLabelTo !== null}
        entityType="charger"
        entityId={addingLabelTo?.id ?? 0}
        onClose={() => setAddingLabelTo(null)}
        onSaved={() => {
          if (addingLabelTo) loadLabels(addingLabelTo);
          setAddingLabelTo(null);
        }}
      />
    </>
  );
}
